import { useQuery } from '@tanstack/react-query';
import { axiosClient } from '../config';
import { z } from 'zod';
import { ApiResponseWithMetaData, CustomQueryOptions } from '@/shared/types';
import {
  DtoPointHistoryFindType,
  DtoPointHistoryType,
} from '@/shared/constants';

const DtoPointHistorySortType = {
  desc: 'createdAt,DESC',
  asc: 'createdAt,ASC',
} as const;

export const getPointHistoryRequestSchema = z.object({
  spaceId: z.number(),
  page: z.number(),
  size: z.number().default(10),
  sort: z
    .nativeEnum(DtoPointHistorySortType)
    .default(DtoPointHistorySortType.desc),
  filterType: z.nativeEnum(DtoPointHistoryFindType).optional(),
});

export const getPointHistoryResponseSchema = z.object({
  id: z.number(),
  spaceId: z.number(),
  projectId: z.number().optional(),
  accountId: z.number(),
  type: z.nativeEnum(DtoPointHistoryType),
  changeValue: z.number(),
  remainingValue: z.number(),
  createdAt: z.string().datetime(),
});

export type GetPointHistoryRequestType = z.infer<
  typeof getPointHistoryRequestSchema
>;
export type GetPointHistoryResponseType = z.infer<
  typeof getPointHistoryResponseSchema
>;

type ResponseType = ApiResponseWithMetaData<GetPointHistoryResponseType[]>;

export const getPointHistoryApi = async (
  request: GetPointHistoryRequestType,
) => {
  const response = await axiosClient.get<ResponseType>(
    `/v1.0/point/history/${request.spaceId}`,
    {
      params: {
        page: request.page,
        size: request.size,
        sort: request.sort,
        filterType: request.filterType,
      },
    },
  );

  return response.data;
};

export const QueryKeyUseGetPointHistoryApi = 'getPointHistory' as const;

export const useGetPointHistoryApi = (
  request: GetPointHistoryRequestType,
  queryOptions?: CustomQueryOptions<ResponseType>,
) => {
  return useQuery({
    queryKey: [QueryKeyUseGetPointHistoryApi, request],
    queryFn: () => getPointHistoryApi(request),
    ...queryOptions,
  });
};
